import type { MRT_Column, MRT_ColumnDef, MRT_Row, MRT_RowData } from "material-react-table";
import { createContext, isValidElement, useContext, useState, type ReactNode } from "react";
import CondensedCell, {
  FoldedValuesClaimContext,
  FoldedValuesContext,
  type FoldedValue,
} from "_shared/_views/tables/condensed-cell";
import { COLUMN_ID_ATTRIBUTE } from "_shared/_views/tables/table-column-widths";

/**
 * How a list table draws the columns the fit folded (#6711): off the row, and onto their hosts' second
 * lines. The fit decides which fold (`foldColumns` in `table-column-fit.ts`); this is the drawing.
 *
 * A folded column stays visible to MRT. That is what keeps it switched on in Show/hide: the reader
 * still has it, in the only place the pane has room for. Its own cells are taken out of the layout
 * instead, and its value is read into its host's cell.
 */

/** Each folded column, and the host it folds onto. */
export const FoldedColumnsContext = createContext<Readonly<Record<string, string>>>({});

type CellArgs<TData extends MRT_RowData> = Parameters<NonNullable<MRT_ColumnDef<TData>["Cell"]>>[0];

/**
 * A folded column's value, as its host's second line reads it.
 *
 * The column's `fold.render` where it has one. Otherwise its heading and its plain value, "RFP: 3": the
 * column's own `Cell` is not drawn here, since it is laid out for a cell of its own and may keep state
 * that a value coming and going with the pane's width would lose. A column with no plain value and no
 * `fold.render` adds nothing.
 */
export function foldedValueOf<TData extends MRT_RowData>(column: MRT_Column<TData>, row: MRT_Row<TData>): ReactNode {
  const fold = column.columnDef.meta?.fold;
  if (fold?.render) return fold.render(row.original);
  const value = row.getValue<unknown>(column.id);
  if (value === undefined || value === null || value === "") return null;
  if (typeof value !== "string" && typeof value !== "number") return null;
  const { header } = column.columnDef;
  return `${header}: ${value}`;
}

const NO_FOLDED_VALUES: readonly FoldedValue[] = [];

/**
 * A host whose cell is drawn by a component of its own, given a two-line cell around it. A two-line cell
 * drawn inside that component shows the folded values itself and claims them, and this one's second line
 * then leaves them out: the reader sees each value once, on the second line under the name, rather than
 * a third line repeating it (#6722). A component that draws no two-line cell claims nothing, and the
 * values go on this cell's second line.
 */
function WrappedFoldHost(props: Readonly<{ content: ReactNode; values: readonly FoldedValue[] }>) {
  const [isClaimed, setIsClaimed] = useState(false);
  const claim = () => setIsClaimed(true);

  return (
    <FoldedValuesContext.Provider value={isClaimed ? NO_FOLDED_VALUES : props.values}>
      <CondensedCell
        title={
          <FoldedValuesContext.Provider value={props.values}>
            <FoldedValuesClaimContext.Provider value={claim}>{props.content}</FoldedValuesClaimContext.Provider>
          </FoldedValuesContext.Provider>
        }
      />
    </FoldedValuesContext.Provider>
  );
}

function FoldHostCell<TData extends MRT_RowData>(
  props: Readonly<{ args: CellArgs<TData>; drawHost: (args: CellArgs<TData>) => ReactNode }>,
) {
  "use no memo";

  const foldedInto = useContext(FoldedColumnsContext);
  const { column, row, table } = props.args;
  const content = props.drawHost(props.args);
  const values: FoldedValue[] = (table.getVisibleLeafColumns() as MRT_Column<TData>[])
    .filter((folded) => foldedInto[folded.id] === column.id)
    .map((folded) => ({ columnId: folded.id, content: foldedValueOf(folded, row) }));
  if (values.length < 1) return <>{content}</>;

  // A host already drawn as a two-line cell adds the values to its own second line. Any other host is
  // given one, with its content as the title.
  const isCondensed = isValidElement(content) && content.type === CondensedCell;
  if (!isCondensed) return <WrappedFoldHost content={content} values={values} />;
  return <FoldedValuesContext.Provider value={values}>{content}</FoldedValuesContext.Provider>;
}

function hostIds<TData extends MRT_RowData>(columns: readonly MRT_ColumnDef<TData>[], hosts = new Set<string>()) {
  for (const column of columns) {
    if (column.meta?.fold) hosts.add(column.meta.fold.into);
    if (column.columns) hostIds(column.columns, hosts);
  }
  return hosts;
}

function wrapHosts<TData extends MRT_RowData>(
  columns: MRT_ColumnDef<TData>[],
  hosts: ReadonlySet<string>,
): MRT_ColumnDef<TData>[] {
  return columns.map((column) => {
    const nested = column.columns ? { columns: wrapHosts(column.columns, hosts) } : {};
    const id = column.id ?? column.accessorKey;
    if (id === undefined || !hosts.has(String(id))) return { ...column, ...nested };
    const drawOwn = column.Cell;
    const drawHost = (args: CellArgs<TData>) => (drawOwn ? drawOwn(args) : args.renderedCellValue);
    return { ...column, ...nested, Cell: (args: CellArgs<TData>) => <FoldHostCell args={args} drawHost={drawHost} /> };
  });
}

/**
 * The columns, with every column another one can fold onto drawn so that it can carry the folded values.
 * A table where no column folds is handed back as it is.
 */
export function withFoldHosts<TData extends MRT_RowData>(columns: MRT_ColumnDef<TData>[]): MRT_ColumnDef<TData>[] {
  const hosts = hostIds(columns);
  return hosts.size < 1 ? columns : wrapHosts(columns, hosts);
}

/**
 * Takes the folded columns' own cells out of the layout: heading, filter and body alike. Keyed on the
 * column id every list-table cell carries, so a column's own cell props cannot put the cell back.
 */
export function foldedColumnsSx(foldedInto: Readonly<Record<string, string>>) {
  const columnIds = Object.keys(foldedInto);
  if (columnIds.length < 1) return {};
  const selector = columnIds
    .flatMap((columnId) =>
      ["thead", "tbody", "tfoot"].map((part) => `& > ${part} > tr > [${COLUMN_ID_ATTRIBUTE}="${columnId}"]`),
    )
    .join(", ");
  return { [selector]: { display: "none" } };
}
